import React from 'react';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  title?: string;
  style?: React.CSSProperties;
  form?: string;
  /** `data-testid` no botão nativo (usado em testes) */
  dataTestId?: string;
  ariaLabel?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  title,
  style,
  form,
  dataTestId,
  ariaLabel,
}) => {
  const baseClasses =
    'font-bold rounded-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  const mutedWhenDisabled = disabled && !loading;

  const variantClasses = {
    primary: '',
    secondary: 'bg-hivi-surface-soft text-hivi-dark hover:bg-hivi-surface-soft-hover',
    danger: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500',
    ghost:
      'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:ring-gray-500',
  };

  const getVariantStyle = (variant: string) => {
    if (variant === 'primary') {
      return {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-on-primary)',
        '--tw-ring-color': 'var(--color-primary)',
      };
    }
    if (variant === 'secondary') {
      return {
        '--tw-ring-color': 'var(--color-primary)',
      };
    }
    return {};
  };

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-6 py-4 text-lg',
  };

  return (
    <button
      type={type}
      form={form}
      data-testid={dataTestId}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${mutedWhenDisabled ? 'cursor-not-allowed opacity-50' : ''} ${loading ? 'cursor-wait' : ''} ${className}`}
      style={{ ...getVariantStyle(variant), ...style } as React.CSSProperties}
      onMouseEnter={(e) => {
        if (variant === 'primary' && !disabled && !loading) {
          e.currentTarget.style.opacity = '0.9';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
      title={title}
      aria-label={ariaLabel}
    >
      {loading ? (
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
          Carregando...
        </div>
      ) : (
        children
      )}
    </button>
  );
};
